import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, Mail, Users, MessageSquare } from "lucide-react";
import { adminApi, getAdminToken } from "../../lib/adminApi";

const cards = [
  { key: "totalVisits", label: "Page views", icon: Eye },
  { key: "uniqueVisitors", label: "Unique visitors", icon: Users },
  { key: "contacts", label: "Messages", icon: Mail },
  { key: "unreadContacts", label: "Unread", icon: MessageSquare },
];

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi
      .visitStats(getAdminToken())
      .then(setStats)
      .catch((err) => setError(err.message));
  }, []);

  if (error) return <p className="text-[#BE185D]">{error}</p>;
  if (!stats) return <p className="text-black/50">Loading analytics…</p>;

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#EC4899]">Overview</p>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Analytics</h1>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.key} className="rounded-3xl bg-white p-6 border border-black/5 shadow-ambient">
              <Icon size={18} className="text-[#EC4899]" />
              <div className="font-mono text-3xl mt-4">{stats[card.key] ?? 0}</div>
              <div className="text-sm text-black/55 mt-1">{card.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-8 rounded-3xl bg-white p-6 border border-black/5 shadow-ambient">
          <h2 className="font-display text-2xl">Visits · last 14 days</h2>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.last14Days || []}>
                <defs>
                  <linearGradient id="cfPink" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EC4899" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#EC4899" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#EC4899" fill="url(#cfPink)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-4 rounded-3xl bg-white p-6 border border-black/5 shadow-ambient">
          <h2 className="font-display text-2xl">Top pages</h2>
          <ul className="mt-4 space-y-3">
            {(stats.topPages || []).length === 0 && <li className="text-sm text-black/50">No visits yet.</li>}
            {(stats.topPages || []).map((page) => (
              <li key={page.path} className="flex items-center justify-between text-sm">
                <span className="font-mono text-black/80">{page.path}</span>
                <span className="text-[#EC4899] font-medium">{page.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
